<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('attendance_management')" :icon="'calendar-check'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0">
                    <div class="btn-group">
                        <button 
                            type="button" 
                            class="btn btn-primary btn-with-shadow"
                            @click="navigateTo('dashboard')"
                        >
                            <i class="fas fa-tachometer-alt mr-2"></i>
                            {{ $t('dashboard') }}
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-success btn-with-shadow"
                            @click="navigateTo('realtime')"
                        >
                            <i class="fas fa-activity mr-2"></i>
                            {{ $t('real_time') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Overview Cards -->
        <div class="row mb-4">
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--brand text-white" @click="navigateTo('dashboard')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('daily_summary') }}</h6>
                                <p class="mb-0 small">{{ $t('comprehensive_daily_report') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-chart-pie fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--cyan text-white" @click="navigateTo('employee-details')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('employee_details') }}</h6>
                                <p class="mb-0 small">{{ $t('detailed_employee_attendance') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-users fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--dark text-white" @click="navigateTo('monthly-calendar')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('monthly_attendance_calendar') }}</h6>
                                <p class="mb-0 small">{{ $t('monthly_calendar_view') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-calendar-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--indigo text-white" @click="navigateTo('realtime')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('real_time_monitoring') }}</h6>
                                <p class="mb-0 small">{{ $t('live_attendance_tracking') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-activity fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Deuxième rangée -->
        <div class="row mb-4">
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--sky text-white" @click="navigateTo('reports')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('attendance_reports') }}</h6>
                                <p class="mb-0 small">{{ $t('generate_export_reports') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-file-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 mb-3">
                <div class="card overview-card overview-card--emerald text-white" @click="navigateTo('worksite-map')">
                    <div class="card-body">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('attendance_by_worksite') }}</h6>
                                <p class="mb-0 small">{{ $t('attendance_by_worksite_short_intro') }}</p>
                            </div>
                            <div class="overview-icon">
                                <i class="fas fa-map-marked-alt fa-2x"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Today's Quick Stats -->
        <div class="row mb-4" v-if="todayStats">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('todays_overview') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3 text-center">
                                <h4 class="text-success">{{ formatCountValue(todayStats.present_employees || 0) }}</h4>
                                <p class="mb-0">{{ $t('present_employees') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-danger">{{ formatCountValue(todayStats.absent_employees || 0) }}</h4>
                                <p class="mb-0">{{ $t('absent_employees') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-warning">{{ formatCountValue(todayStats.late_arrivals || 0) }}</h4>
                                <p class="mb-0">{{ $t('late_arrivals') }}</p>
                            </div>
                            <div class="col-md-3 text-center">
                                <h4 class="text-info">{{ formatCountValue(todayStats.overtime_employees || 0) }}</h4>
                                <p class="mb-0">{{ $t('overtime_employees') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Actions -->
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">{{ $t('quick_actions') }}</h5>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-primary btn-block" @click="navigateTo('dashboard')">
                                    <i class="fas fa-tachometer-alt mr-2"></i>
                                    {{ $t('view_dashboard') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-success btn-block" @click="navigateTo('employee-details')">
                                    <i class="fas fa-users mr-2"></i>
                                    {{ $t('employee_details') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-warning btn-block" @click="navigateTo('realtime')">
                                    <i class="fas fa-activity mr-2"></i>
                                    {{ $t('real_time_monitoring') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-info btn-block" @click="exportTodayReport">
                                    <i class="fas fa-file-export mr-2"></i>
                                    {{ $t('export_today_report') }}
                                </button>
                            </div>
                            <div class="col-md-3 mb-3">
                                <button type="button" class="btn btn-outline-dark btn-block" @click="navigateTo('worksite-map')">
                                    <i class="fas fa-map-marked-alt mr-2"></i>
                                    {{ $t('attendance_by_worksite') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { ATTENDANCE_DAILY_SUMMARY, ATTENDANCE_EXPORT_SUMMARY } from '../../../Config/ApiUrl';

export default {
    extends: CoreLibrary,
    name: "AttendanceNavigation",
    data() {
        return {
            todayStats: null
        }
    },
    mounted() {
        this.loadTodayStats();
    },
    methods: {
        navigateTo(route) {
            const routes = {
                'dashboard': '/attendance/dashboard-summary',
                'employee-details': '/attendance/employee-details',
                'monthly-calendar': '/attendance/monthly-calendar',
                'realtime': '/attendance/real-time-stats',
                'reports': '/attendance/reports',
                'worksite-map': '/attendance/worksite-map',
            };
            
            if (routes[route]) {
                window.location.href = routes[route];
            }
        },

        async loadTodayStats() {
            try {
                const response = await this.axiosGet(ATTENDANCE_DAILY_SUMMARY);
                this.todayStats = response.data;
            } catch (error) {
                console.error('Error loading today stats:', error);
            }
        },

        exportTodayReport() {
            const today = window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0];
            const url = `/attendances/export-summary?date=${today}`;
            window.open(url, '_blank');
        }
    }
}
</script>

<style scoped>
.overview-card {
    cursor: pointer;
    transition: transform var(--mozali-motion-micro) var(--mozali-motion-ease),
    box-shadow var(--mozali-motion-standard) var(--mozali-motion-ease);
    border: none;
    border-radius: var(--mozali-radius-lg);
}

.overview-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--mozali-shadow-card-hover);
}

.overview-icon {
    opacity: 0.8;
}

.card {
    border-radius: var(--mozali-radius-lg);
    box-shadow: var(--mozali-shadow-card);
}

.overview-card--brand {
    background: var(--mozali-brand-gradient);
}

.overview-card--cyan {
    background: linear-gradient(135deg, var(--mozali-color-brand-dark) 0%, var(--mozali-color-cyan) 100%);
}

.overview-card--indigo {
    background: linear-gradient(135deg, var(--mozali-color-indigo) 0%, var(--mozali-color-brand-dark) 100%);
}

.overview-card--dark {
    background: var(--mozali-dark-gradient);
}

.overview-card--sky {
    background: linear-gradient(135deg, var(--mozali-color-brand) 0%, var(--mozali-color-cyan) 100%);
}

.overview-card--emerald {
    background: linear-gradient(135deg, #047857 0%, #0f766e 100%);
}

@media (prefers-reduced-motion: reduce) {
    .overview-card {
        transition: none !important;
    }

    .overview-card:hover {
        transform: none !important;
    }
}
</style>
